/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    Text,
    Image,
    Box,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {createAccountButton, formErrorMessageStyle, topHeader} from '../register/index.style'
import {headingText} from '../reset-password/index.style'
import ResetPasswordLandingFields from '../forms/reset-password-landing-fields'

const ResetPasswordLandingForm = ({
    onSave = noop,
    onCancel = noop,
    form,
    hasToken = false,
    email = ''
}) => {
    return (
        <>
            <Stack
                justify="center"
                align="center"
                spacing={{base: '15px', md: '20px'}}
                mb={{md: '5px'}}
                width="100%"
            >
                <Image
                    src={getAssetUrl('static/img/account/my-cavenders.svg')}
                    alt="My Cavenders Logo"
                    width={{base: '205px', md: '265px'}}
                    height={{base: '41px', md: '53px'}}
                />
                <Text align="center" sx={headingText}>
                    <FormattedMessage
                        defaultMessage="Reset Your Password"
                        id="reset_password_landing.title.reset_your_password"
                    />
                </Text>
            </Stack>
            <Box width="100%" mt={{base: '-5px', md: 0}}>
                <form onSubmit={form.handleSubmit(onSave)} data-testid="setpassword-form">
                    <Stack spacing={{base: '20px', md: '25px'}} width="100%">
                        {form.formState.errors?.global && (
                            <Alert status="error">
                                <AlertIcon color="red.500" boxSize={4} />
                                <Text fontSize="sm" ml={3} sx={formErrorMessageStyle}>
                                    {form.formState.errors.global.message}
                                </Text>
                            </Alert>
                        )}

                        <ResetPasswordLandingFields form={form} email={email} />

                        <Flex flexDirection="column" gap="15px" width="100%">
                            <Button
                                type="submit"
                                isLoading={form.formState.isSubmitting}
                                sx={createAccountButton}
                                width="100%"
                                isDisabled={!hasToken}
                            >
                                <FormattedMessage
                                    defaultMessage="Save"
                                    id="reset_password_landing.button.save"
                                />
                            </Button>
                            <Button
                                variant="outline"
                                onClick={onCancel}
                                sx={{
                                    ...createAccountButton,
                                    backgroundColor: 'white',
                                    color: 'cav_red',
                                    borderColor: 'cav_red',
                                    _hover: {
                                        backgroundColor: 'cav_red',
                                        color: 'white'
                                    },
                                    mt: '15px'
                                }}
                                width="100%"
                            >
                                <FormattedMessage
                                    defaultMessage="Cancel"
                                    id="reset_password_landing.button.cancel"
                                />
                            </Button>
                        </Flex>
                    </Stack>
                </form>
            </Box>
        </>
    )
}

ResetPasswordLandingForm.propTypes = {
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    form: PropTypes.object.isRequired,
    hasToken: PropTypes.bool,
    email: PropTypes.string
}

export default ResetPasswordLandingForm
